import { useState } from "react";
import { Linkedin, Mail } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { useScrollReveal } from "../hooks/useScrollReveal";

const TEAM = [
  { key: "owner",      image: "/generated/team_owner_portrait.png", fallback: "GF" },
  { key: "operations", image: "/generated/team_operations_portrait.png", fallback: "BL" },
  { key: "logistics",  image: "/generated/team_logistics_portrait.png", fallback: "TL" },
  { key: "cleaning",   image: "/generated/team_cleaning_portrait.png", fallback: "TR" },
];

function TeamCard({ member, t, idx }) {
  const [ref, visible] = useScrollReveal();
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div
      ref={ref}
      className={`group card-lift bg-white border border-neutral-200 overflow-hidden rounded-sm sr sr-scale sr-d${idx + 1} ${visible ? "is-visible" : ""}`}
      data-testid={`team-card-${member.key}`}
    >
      <div className="aspect-[4/5] overflow-hidden bg-neutral-200 relative">
        {!imgFailed ? (
          <img
            src={member.image}
            alt={t.team[`${member.key}_role`]}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FF5A00] to-[#9A2A00] text-white">
            <span className="text-7xl sm:text-8xl font-black tracking-tighter opacity-90" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              {member.fallback}
            </span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
        <div className="absolute bottom-4 inset-x-5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <a href="mailto:info@talkani.de" aria-label="Email" className="w-9 h-9 bg-white text-[#0A0B0D] hover:bg-[#FF5A00] hover:text-white flex items-center justify-center rounded-sm transition-colors">
            <Mail size={16} />
          </a>
          <a href="#contact" aria-label="LinkedIn" className="w-9 h-9 bg-white text-[#0A0B0D] hover:bg-[#FF5A00] hover:text-white flex items-center justify-center rounded-sm transition-colors">
            <Linkedin size={16} />
          </a>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">{t.team[`${member.key}_tag`]}</div>
        <h3 className="text-lg font-bold tracking-tight mb-1">{t.team[`${member.key}_role`]}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed">{t.team[`${member.key}_bio`]}</p>
      </div>
    </div>
  );
}

export default function TeamSection() {
  const { t } = useLang();
  const [headRef, headVisible] = useScrollReveal();
  return (
    <section className="py-24 lg:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headRef} className={`mb-14 max-w-3xl sr ${headVisible ? "is-visible" : ""}`}>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.team.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">{t.team.title}</h2>
          <p className="text-base text-neutral-600 leading-relaxed">{t.team.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((m, i) => (
            <TeamCard key={m.key} member={m} t={t} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
